<template>
  <v-container>
    <v-row :class="classes">
      <v-col cols="12" sm="10">
        <video
          ref="videoPlayer"
          autoplay
          muted
          style="width: 100%; max-width: 800px;"
        ></video>
      </v-col>
      <v-col cols="12" sm="2">
        <v-btn-toggle v-model="controlMode" mandatory borderless>
          <v-btn value="presets">Suunta</v-btn>
          <v-btn value="iot">Patteri</v-btn>
        </v-btn-toggle>

        <div v-if="controlMode === 'presets'">
          <v-list>
            <v-list-item v-for="preset in presets" :key="preset" @click="change(preset)">
              <v-list-item-content>
                <v-list-item-title>{{ preset }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-btn @click="screenshot" outlined color="primary">Tallenna kuva</v-btn>
        </div>

        <div v-if="controlMode === 'iot'">
          <v-alert outlined class="mt-4" type="info" :value="true" v-if="!shelly || !shelly.plugData.online">Ei yhteyttä rasiaan</v-alert>
          <v-switch v-if="plugState != null" :disabled="!shelly || !shelly.plugData.online" :loading="loading" label="Patterin virta" v-model="plugState"></v-switch>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="status === false">
      <v-col cols="12">
        <v-alert  prominent :value="true" type="error" outlined>
          <v-row align="center">
            <v-col class="grow">Ei yhteyttä Rankkuuseen</v-col>
            <v-col class="shrink">
              <v-btn outlined color="error" @click="tryAgain">Yritä uudestaan</v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>


<script>
import { DateTime, Duration } from 'luxon';
import api from "@/api";
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'Video',
  emits: ['shelly-plug-state-changed'],
  data: () => ({
    controlMode: 'presets',
    msg: '',
    presets: [],
    status: null,
    // MSE-related
    ws: null,
    mediaSource: null,
    latencyTimer: null,
  }),
  methods: {
    ...mapActions('shelly', ['fetchShellyData', 'setShellyPlugState']),
    initPlayer() {
      const video = this.$refs.videoPlayer
      const self = this

      // Latency control constants - tune for your network
      const TARGET_LATENCY = 2.0   // Seconds behind live edge (more buffer room)
      const MAX_LATENCY = 4.0      // If more behind than this, jump forward
      const KEEP_BUFFER = 6.0      // Total buffer to keep in SourceBuffer
      const MAX_BEHIND_RESET = 60  // Seconds behind live edge before reset
      const WRAP_BACKWARD_JUMP = 1.0
      const MAX_QUEUE = 30

      // Helper: Get live edge (newest buffered timestamp)
      function liveEdge(sb) {
        const b = sb.buffered
        return b.length ? b.end(b.length - 1) : null
      }

      let lastLiveEdge = null
      let resetting = false

      const resetStream = (reason) => {
        if (resetting) return
        resetting = true
        console.warn('Resetting stream:', reason)
        self.destroyPlayer()
        setTimeout(() => {
          resetting = false
          self.initPlayer()
        }, 500)
      }

      // Core low-latency logic: keep player near live edge
      function maintainLatency(video, sb) {
        if (!sb || sb.updating) return
        if (!self.mediaSource || self.mediaSource.readyState !== 'open') return
        const le = liveEdge(sb)
        if (le == null || !isFinite(video.currentTime)) return
        if (lastLiveEdge != null && le < lastLiveEdge - WRAP_BACKWARD_JUMP) {
          resetStream(`timestamp wrap: live edge moved backward ${lastLiveEdge - le}s`)
          return
        }
        lastLiveEdge = le

        const behind = le - video.currentTime
        if (behind > MAX_BEHIND_RESET) {
          if (video.currentTime < 0.5) {
            console.log(`Initial catch-up from ${behind.toFixed(2)}s behind`)
            video.currentTime = Math.max(0, le - TARGET_LATENCY)
            return
          }
          resetStream(`stalled behind ${behind.toFixed(2)}s`)
          return
        }

        // 1) Hard catch-up if we drift too far
        if (behind > MAX_LATENCY) {
          console.log(`Behind by ${behind.toFixed(2)}s, jumping to live edge`)
          video.currentTime = Math.max(0, le - TARGET_LATENCY)
        }
        video.playbackRate = 1.0

        // Trim buffer to prevent delay accumulation
        try {
          const b = sb.buffered
          if (b.length) {
            const start = b.start(0)
            const end = b.end(b.length - 1)
            if (end - start > KEEP_BUFFER) {
              sb.remove(start, end - KEEP_BUFFER)
            }
          }
        } catch (err) {
          // Ignore remove errors (normal during updateend)
        }
      }

      // Create MediaSource
      const mediaSource = new MediaSource()
      video.src = URL.createObjectURL(mediaSource)
      this.mediaSource = mediaSource

      mediaSource.addEventListener('sourceopen', () => {
        console.log('MediaSource opened')

        // Determine codec string for H.264
        const codecCandidates = [
          'video/mp4; codecs="avc1.640029"',  // H.264 High@4.1
          'video/mp4; codecs="avc1.640028"',  // H.264 High@4.0
          'video/mp4; codecs="avc1.42E01E"',  // H.264 Baseline@3.0
        ]

        const codec = codecCandidates.find(c => MediaSource.isTypeSupported(c))
        if (!codec) {
          console.error('No supported H.264 codec found')
          return
        }

        console.log('Using codec:', codec)
        const sourceBuffer = mediaSource.addSourceBuffer(codec)
        sourceBuffer.mode = 'segments'

        // Fragment queue
        const queue = []
        let appending = false

        // WebSocket connection
        const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/stream`
        console.log('Connecting to WebSocket:', wsUrl)
        const ws = new WebSocket(wsUrl)
        ws.binaryType = 'arraybuffer'
        this.ws = ws

        // Append next fragment from queue
        function appendNext() {
          if (appending || sourceBuffer.updating || queue.length === 0) return
          appending = true
          try {
            sourceBuffer.appendBuffer(new Uint8Array(queue.shift()))
          } catch (err) {
            if (err && err.name === 'QuotaExceededError') {
              console.warn('SourceBuffer quota exceeded, trimming buffer')
              try {
                const b = sourceBuffer.buffered
                if (b.length) {
                  sourceBuffer.remove(b.start(0), b.end(b.length - 1))
                }
              } catch (removeErr) {
                console.warn('SourceBuffer trim failed, resetting stream')
                resetStream('quota exceeded')
                return
              }
              appending = false
              return
            }
            console.warn('SourceBuffer append failed, resetting stream')
            resetStream('append failed')
            return
          }
        }

        // Handle append completion
        sourceBuffer.addEventListener('updateend', () => {
          appending = false

          // CRITICAL: Maintain low latency after each append
          maintainLatency(video, sourceBuffer)

          appendNext()
        })

        // Handle buffer underrun: skip ahead instead of showing loading spinner
        video.addEventListener('waiting', () => {
          if (!self.mediaSource || self.mediaSource.readyState !== 'open') return
          const le = liveEdge(sourceBuffer)
          if (le != null && le > video.currentTime) {
            console.log('Skipping ahead due to buffer underrun')
            video.currentTime = Math.max(0, le - TARGET_LATENCY)
          }
        })

        video.addEventListener('error', () => {
          resetStream('video element error')
        })

        // Receive fragments from WebSocket
        ws.onopen = () => {
          console.log('WebSocket connected')
        }

        ws.onmessage = (event) => {
          queue.push(event.data)
          if (queue.length > MAX_QUEUE) {
            queue.splice(0, queue.length - MAX_QUEUE)
          }
          appendNext()

          const le = liveEdge(sourceBuffer)
          if (le != null && (video.currentTime < 0.5 || !isFinite(video.currentTime))) {
            console.log('Seeking to live edge on startup')
            video.currentTime = Math.max(0, le - TARGET_LATENCY)
          }

          // Start playing once we have some buffer
          if (video.paused && queue.length === 0 && le != null) {
            video.play().catch(e => console.log('Autoplay prevented:', e))
          }
        }

        ws.onerror = (error) => {
          console.error('WebSocket error:', error)
        }

        ws.onclose = () => {
          console.log('WebSocket closed, attempting reconnect...')
          if (resetting) return
          setTimeout(() => {
            this.destroyPlayer()
            this.initPlayer()
          }, 2000)
        }

        // CRITICAL: Periodic latency maintenance (every 500ms)
        // This catches drift even between appends
        const latencyTimer = setInterval(() => {
          maintainLatency(video, sourceBuffer)
        }, 500)

        this.latencyTimer = latencyTimer
      })

      mediaSource.addEventListener('sourceclose', () => {
        console.log('MediaSource closed')
      })

      mediaSource.addEventListener('error', () => {
        resetStream('MediaSource error')
      })
    },
    destroyPlayer() {
      // Clean up WebSocket
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }

      // Clean up latency maintenance timer
      if (this.latencyTimer) {
        clearInterval(this.latencyTimer)
        this.latencyTimer = null
      }

      // Clean up MediaSource
      if (this.mediaSource) {
        this.mediaSource = null
      }
    },
    tryAgain() {
      window.location.reload()
    },
    async getData() {
      const data = await api.data();
      if (data.heartbeat == null) {
        return;
      }
      const heartbeat = DateTime.fromMillis(data.heartbeat);
      const now = DateTime.local().minus(Duration.fromObject({minutes: 3}));

      if (heartbeat < now) {
        this.status = false;
      } else {
        this.status = true;
      }
    },
    // setShellyPlugState is now provided by mapActions
    // The original implementation is now in the Vuex store
    screenshot() {
      const uniqString = DateTime.local().toFormat('yyyy-MM-dd_HH:mm:ss');
      const video = this.$refs.videoPlayer;

      if (!video) {
        return;
      }

      // Create a canvas to capture the video frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/jpeg');
      a.download = 'rankkuu_' + uniqString + '.jpg';
      a.click();
      a.remove();
    },
    async getPresets() {
      this.presets = await api.getPresets();
    },
    async change(action) {
      await api.goToPreset(action);
    }
  },
  computed: {
    ...mapGetters('shelly', ['shellyData', 'isLoading']),
    shelly() {
      return this.shellyData;
    },
    plugState: {
      get() {
        return this.shelly && this.shelly.plugData.online ? this.shelly.plugData.output : null;
      },
      set(value) {
        if (this.plugState !== null && this.plugState !== value) {
          this.setShellyPlugState(value);
        }
      }
    },
    loading() {
      return this.isLoading;
    },
    classes() {
      const ret = ['text-center'];
//      if (this.status !== true) {
//        return [...ret, 'd-none'];
//      }
      return ret;
    }
  },
  async mounted() {
    this.getData();
    await this.getPresets();
    this.initPlayer();
  },
  beforeDestroy() {
    this.destroyPlayer();
  }
}
</script>

<style scoped>
video {
  background: black;
}
</style>
